import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { ChevronRight, ImageOff, Loader2, Search } from "lucide-react";
import { ActressCard } from "@/components/ActressCard.tsx";
import { PaginationNav } from "@/components/PaginationNav.tsx";
import { fetchActressByBwh } from "@/lib/api.ts";
import { BUST_RANGES, WAIST_RANGES, HIP_RANGES, SORT_OPTIONS, type BwhCategory } from "@/lib/types.ts";
import type { Actress } from "@/lib/types.ts";

const categoryLabels: Record<BwhCategory, string> = {
  bust: "バスト",
  waist: "ウエスト",
  hip: "ヒップ",
};

const categoryNavLabels: Record<BwhCategory, string> = {
  bust: "バスト別",
  waist: "ウエスト別",
  hip: "ヒップ別",
};

const defaultSortFor: Record<BwhCategory, string> = {
  bust: "youngest",
  waist: "youngest",
  hip: "youngest",
};

function getRanges(category: BwhCategory) {
  switch (category) {
    case "bust": return BUST_RANGES;
    case "waist": return WAIST_RANGES;
    case "hip": return HIP_RANGES;
  }
}

export function BwhRangePage() {
  const { category, range } = useParams<{ category: string; range: string }>();
  const location = useLocation();
  const pathCategory = location.pathname.split("/")[1] as BwhCategory;
  const bwhCategory = (category as BwhCategory) || pathCategory || "bust";
  const ranges = getRanges(bwhCategory);

  const currentRange = ranges.find((r) => r.label === range) || ranges[0]!;

  const [actresses, setActresses] = useState<Actress[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [showNoImage, setShowNoImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(defaultSortFor[bwhCategory] || "bust");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchActressByBwh(
        bwhCategory,
        currentRange.min,
        currentRange.max,
        page,
        12,
        !showNoImage,
        sortBy,
        search || undefined
      );
      setActresses(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } finally {
      setLoading(false);
    }
  }, [bwhCategory, currentRange.min, currentRange.max, page, showNoImage, sortBy, search]);

  useEffect(() => {
    setPage(1);
    setSortBy(defaultSortFor[bwhCategory] || "bust");
    setSearch("");
    setSearchInput("");
  }, [bwhCategory, range]);

  useEffect(() => {
    setPage(1);
  }, [sortBy, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const doSearch = () => {
    setSearch(searchInput);
  };

  const sortLabel = SORT_OPTIONS.find((s) => s.value === sortBy)?.label ?? sortBy;

  return (
    <div className="flex flex-col flex-1">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 px-4 md:px-8 lg:px-16 py-5">
        <Link to="/" className="font-[var(--font-secondary)] text-[13px] text-muted-foreground no-underline hover:text-foreground">
          トップ
        </Link>
        <ChevronRight size={14} className="text-muted-foreground" />
        <span className="font-[var(--font-secondary)] text-[13px] text-muted-foreground">
          {categoryNavLabels[bwhCategory]}
        </span>
        <ChevronRight size={14} className="text-muted-foreground" />
        <span className="font-[var(--font-secondary)] text-[13px] font-medium text-foreground">
          {currentRange.label}cm
        </span>
      </div>

      {/* Hero Section */}
      <div className="bg-background px-4 md:px-8 lg:px-16 pt-6 pb-5 flex flex-col gap-4">
        <h1 className="font-[var(--font-secondary)] text-2xl md:text-4xl font-bold text-foreground">
          {categoryLabels[bwhCategory]} {currentRange.label}cm の女優一覧
        </h1>

        {/* Search + Sort + Toggle */}
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="女優名で検索..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-border bg-card font-[var(--font-secondary)] text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-9 px-3 rounded-lg border border-border bg-card font-[var(--font-secondary)] text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={() => { setShowNoImage(!showNoImage); setPage(1); }}
              className={`flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium border cursor-pointer transition-colors shrink-0 ${
                showNoImage
                  ? "bg-primary/10 text-primary border-primary/30"
                  : "bg-card text-muted-foreground border-border hover:bg-secondary"
              }`}
            >
              <ImageOff size={14} />
              <span className="hidden sm:inline">サムネなしも表示</span>
            </button>
          </div>
        </div>

        <p className="font-[var(--font-secondary)] text-[13px] text-muted-foreground m-0">
          {search ? `「${search}」の検索結果 - ` : ""}{categoryLabels[bwhCategory]}{currentRange.min}〜{currentRange.max}cm・{sortLabel}・全 {total} 件
        </p>

        {/* Range Chips */}
        <div className="flex gap-2 items-center flex-wrap">
          {ranges.map((r) => (
            <Link
              key={r.label}
              to={`/${bwhCategory}/${r.label}`}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium no-underline transition-colors ${
                r.label === currentRange.label
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "bg-secondary text-secondary-foreground hover:bg-muted"
              }`}
            >
              {r.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="px-4 md:px-8 lg:px-16 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {actresses.map((actress) => (
                <ActressCard key={actress.id} actress={actress} />
              ))}
            </div>
            {actresses.length === 0 && (
              <div className="text-center py-20 text-muted-foreground font-[var(--font-secondary)]">
                該当する女優が見つかりませんでした
              </div>
            )}
          </>
        )}
      </div>

      <PaginationNav currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
